import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getTranslatedProductName } from '../../utils/translationHelpers';

// Types
interface OrderHistoryItem {
  id: string;
  orderId: string;
  poNumber?: string; // PO Number (same as Order Number) or timestamp format
  customerName: string;
  customerEmail: string;
  orderDate: Timestamp;
  totalAmount: number;
  totalItems: number;
  status: 'completed' | 'pending' | 'approved' | 'cancelled';
  orderType: 'custOrder' | 'offCarDelivery';
  orderTypeLabel?: string; // 'Store Order' or 'Supply Order'
  products: Array<{
    category: string;
    name: string;
    quantity: number;
    price: number;
  }>;
  supplyItems?: Array<any>; // Supply order items if applicable
}

interface StoreHistoryProps {
  storeId: string;
  storeName: string;
  onOrderClick?: (orderId: string) => void;
}

// Styled Components
const HistoryContainer = styled.div`
  background: ${ThemeColors.background.paper};
  border-radius: ${BorderRadius.xl};
  box-shadow: ${Shadows.md};
  overflow: hidden;
`;

const HistoryHeader = styled.div`
  background: linear-gradient(135deg, ${ThemeColors.primary.main} 0%, ${ThemeColors.primary.dark} 100%);
  color: white;
  padding: ${Spacing.md} ${Spacing.lg};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 4s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1) rotate(0deg); }
    50% { transform: scale(1.1) rotate(180deg); }
  }
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm} ${Spacing.md};
  }
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 1;
`;

const HistoryTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-family: ${Typography.fontFamily.secondary};
  color: white;
  margin: 0 0 ${Spacing.xs} 0;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.lg};
  }
`;

const HistorySubtitle = styled.p`
  font-size: ${Typography.fontSize.md};
  font-family: ${Typography.fontFamily.primary};
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.sm};
  }
`;

const ControlsSection = styled.div`
  padding: ${Spacing.md} ${Spacing.lg};
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid ${ThemeColors.border};
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.md};
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm} ${Spacing.md};
    gap: ${Spacing.sm};
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 300px;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 2px solid ${ThemeColors.border};
  border-radius: ${BorderRadius.xl};
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 4px rgba(70, 130, 78, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
  
  &::placeholder {
    color: ${ThemeColors.text.secondary};
    font-style: italic;
  }
  
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    padding: ${Spacing.xs} ${Spacing.sm};
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const SortSelect = styled.select`
  min-width: 180px;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 2px solid ${ThemeColors.border};
  border-radius: ${BorderRadius.xl};
  font-size: 14px;
  background: white;
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 4px rgba(70, 130, 78, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
  
  &:hover {
    border-color: ${ThemeColors.primary.main};
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    min-width: 100%;
    width: 100%;
    padding: ${Spacing.xs} ${Spacing.sm};
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const HistoryContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  padding: ${Spacing.md} ${Spacing.lg};
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${ThemeColors.primary.main};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${ThemeColors.primary.dark};
  }
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm} ${Spacing.xs};
    max-height: calc(100vh - 200px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.lg};
  color: ${ThemeColors.text.secondary};
  
  @media (max-width: 768px) {
    padding: ${Spacing.md} ${Spacing.sm};
  }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${Spacing.sm};
  opacity: 0.5;
  
  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: ${Spacing.xs};
  }
`;

const EmptyTitle = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-family: ${Typography.fontFamily.secondary};
  color: ${ThemeColors.text.secondary};
  margin: 0 0 ${Spacing.xs} 0;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.md};
  }
`;

const EmptyDescription = styled.p`
  font-size: ${Typography.fontSize.md};
  font-family: ${Typography.fontFamily.primary};
  color: ${ThemeColors.text.secondary};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.sm};
  }
`;

// Timeline Components
const TimelineContainer = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, ${ThemeColors.primary.main}, ${ThemeColors.accent.main});
  }
  
  @media (max-width: 768px) {
    &::before {
      left: 12px;
      width: 2px;
    }
  }
`;

const TimelineItem = styled.div<{ $isLast?: boolean }>`
  position: relative;
  padding-left: 60px;
  margin-bottom: ${Spacing.md};
  
  &::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 8px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${ThemeColors.primary.main};
    border: 3px solid ${ThemeColors.background.paper};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.main}20;
    z-index: 2;
  }
  
  ${props => props.$isLast && `
    &::before {
      background: ${ThemeColors.accent.main};
      box-shadow: 0 0 0 3px ${ThemeColors.accent.main}20;
    }
  `}
  
  @media (max-width: 768px) {
    padding-left: 40px;
    margin-bottom: ${Spacing.sm};
    
    &::before {
      left: 3px;
      top: 6px;
      width: 14px;
      height: 14px;
      border: 2px solid ${ThemeColors.background.paper};
      box-shadow: 0 0 0 2px ${ThemeColors.primary.main}20;
    }
  }
`;

const OrderCard = styled.div`
  background: white;
  border: 1px solid ${ThemeColors.border};
  border-radius: ${BorderRadius.xl};
  padding: ${Spacing.md} ${Spacing.lg};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${ThemeColors.primary.main}, ${ThemeColors.accent.main});
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(0, 0, 0, 0.06);
    border-color: ${ThemeColors.primary.main};
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  &:active {
    transform: translateY(-2px);
    transition: transform 0.1s ease;
  }
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm};
    border-radius: ${BorderRadius.lg};
    
    &:hover {
      transform: none; /* Disable hover transform on mobile */
    }
    
    &:active {
      transform: scale(0.98);
      transition: transform 0.1s ease;
    }
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.sm};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${Spacing.xs};
    margin-bottom: ${Spacing.xs};
  }
`;

const OrderInfo = styled.div`
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const OrderId = styled.h4`
  font-size: ${Typography.fontSize.lg};
  font-family: ${Typography.fontFamily.secondary};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
  font-weight: 700;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.md};
    word-break: break-all;
  }
`;

const CustomerName = styled.p`
  font-size: ${Typography.fontSize.md};
  font-family: ${Typography.fontFamily.primary};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.sm};
  }
`;

const OrderDate = styled.p`
  font-size: ${Typography.fontSize.sm};
  font-family: ${Typography.fontFamily.primary};
  color: ${ThemeColors.text.secondary};
  margin: 0;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const OrderMeta = styled.div`
  text-align: right;
  
  @media (max-width: 768px) {
    text-align: left;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: ${Spacing.xs};
    border-top: 1px solid ${ThemeColors.border.light};
  }
`;

const OrderTotal = styled.div`
  font-size: ${Typography.fontSize.xl};
  font-family: ${Typography.fontFamily.secondary};
  color: ${ThemeColors.primary.main};
  font-weight: 700;
  margin-bottom: ${Spacing.xs};
  letter-spacing: -0.01em;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.lg};
    margin-bottom: 0;
  }
`;

const OrderItems = styled.div`
  font-size: ${Typography.fontSize.sm};
  font-family: ${Typography.fontFamily.primary};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.sm};
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 12px;
    margin-bottom: 0;
  }
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: ${Spacing.sm} ${Spacing.md};
  border-radius: ${BorderRadius.xl};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.$status) {
      case 'completed':
      case 'approved':
        return `
          background: linear-gradient(135deg, #d4edda, #c3e6cb);
          color: #155724;
          border: 1px solid #c3e6cb;
          box-shadow: 0 2px 4px rgba(21, 87, 36, 0.1);
        `;
      case 'pending':
        return `
          background: linear-gradient(135deg, #fff3cd, #ffeaa7);
          color: #856404;
          border: 1px solid #ffeaa7;
          box-shadow: 0 2px 4px rgba(133, 100, 4, 0.1);
        `;
      case 'cancelled':
        return `
          background: linear-gradient(135deg, #f8d7da, #f5c6cb);
          color: #721c24;
          border: 1px solid #f5c6cb;
          box-shadow: 0 2px 4px rgba(114, 28, 36, 0.1);
        `;
      default:
        return `
          background: ${ThemeColors.background.default};
          color: ${ThemeColors.text.secondary};
          border: 1px solid ${ThemeColors.border.light};
        `;
    }
  }}
  
  @media (max-width: 768px) {
    padding: 3px ${Spacing.xs};
    font-size: 10px;
    letter-spacing: 0.3px;
  }
`;

const ProductPreview = styled.div`
  margin-top: ${Spacing.md};
  padding-top: ${Spacing.md};
  border-top: 1px solid ${ThemeColors.border};
  
  @media (max-width: 768px) {
    margin-top: ${Spacing.sm};
    padding-top: ${Spacing.sm};
  }
`;

const ProductList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const ProductTag = styled.span`
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  color: ${ThemeColors.text.secondary};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.md};
  font-size: 12px;
  font-weight: 500;
  border: 1px solid ${ThemeColors.border.light};
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, #e9ecef, #dee2e6);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 3px ${Spacing.xs};
    font-size: 11px;
    border-radius: ${BorderRadius.sm};
    
    &:hover {
      transform: none;
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${Spacing.xxl};
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid ${ThemeColors.border};
    border-top: 3px solid ${ThemeColors.primary.main};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.md};
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${Spacing.sm};
    margin-bottom: ${Spacing.sm};
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.xl};
  padding: ${Spacing.md};
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
    border-color: ${ThemeColors.primary.main};
  }
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm};
    border-radius: ${BorderRadius.lg};
    
    &:hover {
      transform: none;
    }
  }
`;

const StatValue = styled.div`
  font-size: ${Typography.fontSize.xl};
  font-family: ${Typography.fontFamily.secondary};
  color: ${ThemeColors.primary.main};
  font-weight: 700;
  margin-bottom: ${Spacing.xs};
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: ${Typography.fontSize.lg};
    margin-bottom: 2px;
  }
`;

const StatLabel = styled.div`
  font-size: ${Typography.fontSize.sm};
  font-family: ${Typography.fontFamily.primary};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 11px;
    letter-spacing: 0.3px;
  }
`;

// Main Component
export const StoreHistory: React.FC<StoreHistoryProps> = ({ storeId, storeName, onOrderClick }) => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState<OrderHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('date-desc');

  // Fetch orders from Firebase
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        
        // Query orders from OrderHistory subcollection (finalized orders only)
        const ordersRef = collection(db, 'Stores', storeId, 'OrderHistory');
        
        // Try to query by finalizedAt first, fallback to createdAt if index doesn't exist
        let querySnapshot;
        try {
          const q = query(
            ordersRef,
            orderBy('finalizedAt', 'desc'), // Order by finalizedAt (most recent first)
            limit(50) // Limit to recent 50 orders for performance
          );
          querySnapshot = await getDocs(q);
        } catch (error: any) {
          // Fallback: if finalizedAt index doesn't exist, query by createdAt
          console.warn('⚠️ finalizedAt index not found, using createdAt:', error);
          const q = query(
            ordersRef,
            orderBy('createdAt', 'desc'),
            limit(50)
          );
          querySnapshot = await getDocs(q);
        }
        const ordersData: OrderHistoryItem[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log('📦 Order data for', doc.id, ':', data);
          
          // Check if this is a supply order
          const isSupplyOrder = Array.isArray(data.supplyItems) && data.supplyItems.length > 0;
          
          // Calculate total items from all item arrays (store orders or supply orders)
          const heritageItems = Array.isArray(data.heritageItems) ? data.heritageItems : [];
          const livingstonItems = Array.isArray(data.livingstonItems) ? data.livingstonItems : [];
          const flowerItems = Array.isArray(data.flowerItems) ? data.flowerItems : [];
          const vegetableItems = Array.isArray(data.vegetableItems) ? data.vegetableItems : [];
          const supplyItems = Array.isArray(data.supplyItems) ? data.supplyItems : [];
          
          console.log('📊 Items breakdown:', {
            heritage: heritageItems.length,
            livingston: livingstonItems.length,
            flower: flowerItems.length,
            vegetable: vegetableItems.length,
            supply: supplyItems.length
          });
          
          const totalItems = isSupplyOrder
            ? supplyItems.reduce((sum: number, item: any) => sum + (parseInt(item.quantity) || 0), 0)
            : heritageItems.reduce((sum: number, item: any) => sum + (parseInt(item.quantity) || 0), 0) +
                           livingstonItems.reduce((sum: number, item: any) => sum + (parseInt(item.quantity) || 0), 0) +
                           flowerItems.reduce((sum: number, item: any) => sum + (parseInt(item.quantity) || 0), 0) +
                           vegetableItems.reduce((sum: number, item: any) => sum + (parseInt(item.quantity) || 0), 0);
          
          // Calculate total amount
          const totalAmount = isSupplyOrder
            ? supplyItems.reduce((sum: number, item: any) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0)), 0)
            : heritageItems.reduce((sum: number, item: any) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0)), 0) +
                            livingstonItems.reduce((sum: number, item: any) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0)), 0) +
                            flowerItems.reduce((sum: number, item: any) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0)), 0) +
                            vegetableItems.reduce((sum: number, item: any) => sum + ((parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0)), 0);
          
          // Combine all products for display
          const allProducts = isSupplyOrder
            ? supplyItems.map((item: any) => ({ ...item, category: item.category || 'Supply' }))
            : [
            ...heritageItems.map((item: any) => ({ ...item, category: 'Heritage' })),
            ...livingstonItems.map((item: any) => ({ ...item, category: 'Livingston' })),
            ...flowerItems.map((item: any) => ({ ...item, category: 'Flower' })),
            ...vegetableItems.map((item: any) => ({ ...item, category: 'Vegetable' }))
          ];
          
          console.log('💰 Calculated totals:', { totalItems, totalAmount, productCount: allProducts.length });
          
          // Format timestamp as YYYYMMDDHHMM
          const formatTimestamp = (date: Date | Timestamp | string | undefined): string | null => {
            if (!date) return null;
            
            let dateObj: Date;
            
            if (date instanceof Date) {
              dateObj = date;
            } else if (typeof date === 'string') {
              dateObj = new Date(date);
            } else if (date && typeof date === 'object' && 'toDate' in date) {
              // Firestore Timestamp
              dateObj = (date as any).toDate();
            } else {
              return null;
            }
            
            if (isNaN(dateObj.getTime())) {
              return null;
            }
            
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            const hours = String(dateObj.getHours()).padStart(2, '0');
            const minutes = String(dateObj.getMinutes()).padStart(2, '0');
            
            return `${year}${month}${day}${hours}${minutes}`;
          };
          
    // Get order date for timestamp formatting
    const orderDate = data.finalizedAt || data.createdAt || data.updatedAt;

    // Always use orderId as the primary identifier (should be timestamp format YYYYMMDDHHMM)
    // If orderId is not in timestamp format, generate it from order date
    let displayId = data.orderId;
    
    // Check if orderId is in timestamp format (12 digits)
    if (!displayId || !/^\d{12}$/.test(displayId)) {
      // Generate timestamp format from order date
      displayId = formatTimestamp(orderDate) || doc.id;
    }
          
          ordersData.push({
            id: doc.id,
            orderId: displayId, // Always timestamp format (YYYYMMDDHHMM)
            poNumber: data.poNumber || '', // PO Number is separate field
            customerName: data.storeName || data.customerName || 'Unknown Customer',
            customerEmail: data.email || data.customerEmail || '',
            orderDate: data.finalizedAt || data.createdAt || data.updatedAt, // Use finalizedAt for OrderHistory
            totalAmount: totalAmount,
            totalItems: totalItems,
            status: data.status || 'pending', // Orders in OrderHistory start as 'pending'
            orderType: data.orderType || 'custOrder',
            products: allProducts,
            orderTypeLabel: isSupplyOrder ? 'Supply Order' : 'Store Order', // Add label to distinguish order types
            supplyItems: isSupplyOrder ? supplyItems : undefined
          });
        });
        
        setOrders(ordersData);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [storeId]);

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let filtered = orders;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.poNumber && order.poNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        order.customerEmail.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return b.orderDate.toMillis() - a.orderDate.toMillis();
        case 'date-asc':
          return a.orderDate.toMillis() - b.orderDate.toMillis();
        case 'amount-desc':
          return b.totalAmount - a.totalAmount;
        case 'amount-asc':
          return a.totalAmount - b.totalAmount;
        default:
          return 0;
      }
    });

    return filtered;
  }, [orders, searchTerm, sortBy]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + order.totalAmount, 0);
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const completedOrders = orders.filter(order => order.status === 'completed').length;

    return {
      totalOrders,
      totalRevenue,
      avgOrderValue,
      completedOrders,
      completionRate: totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0
    };
  }, [orders]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const formatDate = (timestamp: Timestamp) => {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(timestamp.toDate());
  };

  if (loading) {
    return (
      <HistoryContainer>
        <HistoryHeader>
          <HeaderContent>
            <HistoryTitle>{t('orderHistory.title')}</HistoryTitle>
            <HistorySubtitle>Loading {storeName}'s order history...</HistorySubtitle>
          </HeaderContent>
        </HistoryHeader>
        <LoadingSpinner />
      </HistoryContainer>
    );
  }

  return (
    <HistoryContainer>
      <HistoryHeader>
        <HeaderContent>
          <HistoryTitle>{t('orderHistory.title')}</HistoryTitle>
          <HistorySubtitle>
            {stats.totalOrders} orders • {formatCurrency(stats.totalRevenue)} total revenue
          </HistorySubtitle>
        </HeaderContent>
      </HistoryHeader>

      <ControlsSection>
        <SearchInput
          type="text"
          placeholder={t('orderHistory.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        <SortSelect
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="date-desc">📅 {t('orderHistory.newestFirst')}</option>
          <option value="date-asc">📅 {t('orderHistory.oldestFirst')}</option>
          <option value="amount-desc">💰 {t('orderHistory.highestAmount')}</option>
          <option value="amount-asc">💰 {t('orderHistory.lowestAmount')}</option>
        </SortSelect>
      </ControlsSection>

      <HistoryContent>
        {filteredOrders.length === 0 ? (
          <EmptyState>
            <EmptyIcon>📦</EmptyIcon>
            <EmptyTitle>{t('orderHistory.noOrders')}</EmptyTitle>
            <EmptyDescription>
              {searchTerm 
                ? 'No orders match your search criteria. Try adjusting your search terms.'
                : t('orderHistory.noOrdersDesc')
              }
            </EmptyDescription>
          </EmptyState>
        ) : (
          <>
            <StatsGrid>
              <StatCard>
                <StatValue>{stats.totalOrders}</StatValue>
                <StatLabel>{t('orderHistory.totalOrders')}</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{formatCurrency(stats.totalRevenue)}</StatValue>
                <StatLabel>{t('orderHistory.totalRevenue')}</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{formatCurrency(stats.avgOrderValue)}</StatValue>
                <StatLabel>{t('orderHistory.averageOrder')}</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{stats.completionRate.toFixed(1)}%</StatValue>
                <StatLabel>Completion Rate</StatLabel>
              </StatCard>
            </StatsGrid>

            <TimelineContainer>
              {filteredOrders.map((order, index) => (
                <TimelineItem key={order.id} $isLast={index === filteredOrders.length - 1}>
                  <OrderCard onClick={() => onOrderClick?.(order.id)}>
                    <OrderHeader>
                      <OrderInfo>
                        <OrderId>
                          {order.orderId || order.poNumber}
                          {order.orderTypeLabel && (
                            <span style={{ 
                              fontSize: Typography.fontSize.sm, 
                              fontWeight: Typography.fontWeight.normal,
                              color: ThemeColors.text.secondary,
                              marginLeft: Spacing.sm
                            }}>
                              ({order.orderTypeLabel})
                            </span>
                          )}
                        </OrderId>
                        <CustomerName>{order.customerName}</CustomerName>
                        <OrderDate>{formatDate(order.orderDate)}</OrderDate>
                      </OrderInfo>
                      <OrderMeta>
                        <OrderTotal>{formatCurrency(order.totalAmount)}</OrderTotal>
                        <OrderItems>{order.totalItems} items</OrderItems>
                        <StatusBadge $status={order.status}>
                          {order.status}
                        </StatusBadge>
                      </OrderMeta>
                    </OrderHeader>
                    
                    {order.products.length > 0 && (
                      <ProductPreview>
                        <ProductList>
                          {order.products.slice(0, 5).map((product, idx) => (
                            <ProductTag key={idx}>
                              [{product.category.charAt(0)}] {getTranslatedProductName(product.name || 'Product', t)} x{product.quantity}
                            </ProductTag>
                          ))}
                          {order.products.length > 5 && (
                            <ProductTag>
                              +{order.products.length - 5} more
                            </ProductTag>
                          )}
                        </ProductList>
                      </ProductPreview>
                    )}
                  </OrderCard>
                </TimelineItem>
              ))}
            </TimelineContainer>
          </>
        )}
      </HistoryContent>
    </HistoryContainer>
  );
};

export default StoreHistory;
